<template>
    <!-- Edit Columns, shared by decks (CompanyBoard.vue) and meeting boards (Meetings.vue). Edits the columns array in
         place: add, drag to reorder, click a column to change its color, Apply Default Colors. The page saves (or
         cancels) them, and decides whether a column can be removed. -->
    <div class="crm-board-edit-panel">
        <div class="crm-board-edit-title">Columns<span>Drag to reorder<template v-if="hint"> · {{ hint }}</template></span></div>
        <form class="crm-board-edit-add" @submit.prevent="addCol()">
            <input type="text" placeholder="Column name" v-model="addColName" />
            <div class="crm-board-color-select">
                <select v-model="addColColor">
                    <option v-for="(color, i) in $store.state.colorPalette" :key="i" :value="color.hex">{{ color.label }}</option>
                </select>
                <input type="color" v-model="addColColor" title="Custom color" />
            </div>
            <button class="add-btn">Add</button>
        </form>
        <!-- Click a column to change its color (the row under the chips); drag to reorder -->
        <draggable :list="columns" class="crm-board-edit-chips">
            <div :style="'background:' + col.color" v-for="(col, i) in columns" :key="i" :class="pickingColorFor === col ? 'picking' : ''" title="Click to change the color" @click="pickingColorFor = pickingColorFor === col ? null : col">
                <span>{{ col.name }}</span>
                <span class="crm-board-edit-chip-x" title="Remove" @click.stop="$emit('remove', i)"><svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="m12 10.93 5.719-5.72c.146-.146.339-.219.531-.219.404 0 .75.324.75.749 0 .193-.073.385-.219.532l-5.72 5.719 5.719 5.719c.147.147.22.339.22.531 0 .427-.349.75-.75.75-.192 0-.385-.073-.531-.219l-5.719-5.719-5.719 5.719c-.146.146-.339.219-.531.219-.401 0-.75-.323-.75-.75 0-.192.073-.384.22-.531l5.719-5.719-5.72-5.719c-.146-.147-.219-.339-.219-.532 0-.425.346-.749.75-.749.192 0 .385.073.531.219z"/></svg></span>
            </div>
        </draggable>
        <div v-if="pickingColorFor && columns.includes(pickingColorFor)" class="crm-board-edit-color">
            <span>Color for <b>{{ pickingColorFor.name }}</b></span>
            <div class="crm-board-color-select">
                <select v-model="pickingColorFor.color" aria-label="Column color">
                    <option v-for="(color, i) in $store.state.colorPalette" :key="i" :value="color.hex">{{ color.label }}</option>
                </select>
                <input type="color" v-model="pickingColorFor.color" title="Custom color" />
            </div>
            <a class="crm-board-edit-link" style="margin-left:0" @click.prevent="pickingColorFor = null">Done</a>
        </div>
        <div class="crm-board-edit-actions">
            <button type="button" class="btn" @click="$emit('save')">Save</button>
            <button type="button" class="btn-light" @click="$emit('cancel')">Cancel</button>
            <!-- Recolors every column (columnColors.ts); nothing is saved until Save -->
            <a v-if="columns.length" class="crm-board-edit-link" @click.prevent="applyDefaultColors()">Apply Default Colors</a>
        </div>
    </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { VueDraggableNext } from 'vue-draggable-next';

import { NEW_COLUMN_COLOR, applyDefaultColumnColors } from '@/columnColors';

export default defineComponent({
    name: 'EditColumnsPanel',
    // columns: [{ name, color, show }]; the panel reorders and recolors them in place, and asks the page (add, remove) to
    // add or remove one. hint: extra text after "Drag to reorder".
    props: [ 'columns', 'hint' ],
    emits: [ 'save', 'cancel', 'remove', 'add' ],
    components: {
        draggable: VueDraggableNext
    },
    data() {
        return {
            addColName: '',
            addColColor: NEW_COLUMN_COLOR,
            pickingColorFor: null as any // the column whose color is being changed
        }
    },
    methods: {
        addCol() {
            const name = this.addColName.trim();
            if(!name) {
                return;
            }
            if(this.columns.find((c: any) => c.name.toLowerCase() == name.toLowerCase())) {
                alert('There is already a column with this name');
                return;
            }
            // The page adds it to its own columns
            this.$emit('add', { name, color: this.addColColor, show: true });
            this.addColName = '';
        },
        applyDefaultColors() {
            applyDefaultColumnColors(this.columns, this.$store.state.colorPalette);
        }
    }
});
</script>
